import { getImageUrl } from '../utils/imageUtils';

class ImageService {
  constructor() {
    this.cache = new Map();
    this.loadingPromises = new Map();
    this.processedImages = new Set(); // Track which images have been processed
  }

  async getImage(customer, event, imagePath) {
    const cacheKey = `${customer}/${event}/${imagePath}`;
    
    // Return cached image if available
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Return existing loading promise if available
    if (this.loadingPromises.has(cacheKey)) {
      return this.loadingPromises.get(cacheKey);
    }

    // Create new loading promise
    const loadPromise = (async () => {
      try {
        // Remove server prefix from the path if it exists
        const cleanPath = imagePath.replace(/^\/ec2-[^/]+:3000/, '');
        const imageUrl = getImageUrl(customer, event, cleanPath);
        
        // Get the auth token from localStorage
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token available');
        }

        const response = await fetch(imageUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to load image: ${response.statusText}`);
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        
        // Cache the result
        this.cache.set(cacheKey, objectUrl);
        this.loadingPromises.delete(cacheKey);
        this.processedImages.add(cacheKey); // Mark as processed
        
        return objectUrl;
      } catch (error) {
        console.error('Error loading image:', error);
        this.loadingPromises.delete(cacheKey);
        throw error;
      }
    })();

    // Store the loading promise
    this.loadingPromises.set(cacheKey, loadPromise);
    
    return loadPromise;
  }

  // Process a list of images, only downloading new ones
  async processImageList(customer, event, imageList) {
    const downloadPromises = imageList
      .filter(imageName => {
        const cacheKey = `${customer}/${event}/${imageName}`;
        return !this.processedImages.has(cacheKey); // Only download if not processed
      })
      .map(imageName => this.getImage(customer, event, imageName));

    await Promise.all(downloadPromises);
  }

  // Get all cached images for a customer/event
  getCachedImages(customer, event) {
    const prefix = `${customer}/${event}/`;
    const images = [];
    
    this.cache.forEach((url, key) => {
      if (key.startsWith(prefix)) {
        const imageName = key.slice(prefix.length);
        images.push({ name: imageName, url });
      }
    });

    return images;
  }

  // Check if an image has been processed
  isProcessed(customer, event, imageName) {
    const cacheKey = `${customer}/${event}/${imageName}`;
    return this.processedImages.has(cacheKey);
  }

  // Clear cache for a specific customer/event
  clearCache(customer, event) {
    const prefix = `${customer}/${event}/`;
    
    this.cache.forEach((url, key) => {
      if (key.startsWith(prefix)) {
        URL.revokeObjectURL(url);
        this.cache.delete(key);
        this.processedImages.delete(key); // Also remove from processed set
      }
    });
  }

  // Clear all cache
  clearAllCache() {
    this.cache.forEach(url => URL.revokeObjectURL(url));
    this.cache.clear();
    this.loadingPromises.clear();
    this.processedImages.clear(); // Clear processed set
  }
}

const imageService = new ImageService();
export default imageService; 